<template>
  <div class="date-range-with-presets">
    <date-picker-dialog
      range
      v-bind:value="datePickerValue"
      v-on:input="onDatePickerDialogInput"
      v-on:close="onDatePickerDialogClose"
    >
      <template v-slot:trigger="{ listenerObject }">
        <fieldset class="date-range-with-presets__search">
          <label
            class="date-range-with-presets__date"
            title="Выбрать начальную дату"
            v-bind:class="{ 'date-selected': hasSelectedTo, 'custom-color': customColorDate }"
            v-bind:style="cssVars"
          >
            {{ datePickerValue[0] ? $options.formatDate(datePickerValue[0]) : '' }}
            <input
              class="date-range-with-presets__input"
              required
              type="date"
              v-bind:value="datePickerValue[0] ? $options.convertToInputDate(datePickerValue[0]) : ''">
          </label>
          <span v-show="hasSelectedTo">—</span>
          <label
            class="date-range-with-presets__date"
            title="Выбрать конечную дату"
            v-bind:class="{ 'date-selected': hasSelectedTo, 'custom-color': customColorDate }"
            v-bind:style="cssVars"
          >
            {{ dateTo ? $options.formatDate(dateTo) : '' }}
            <input
              class="date-range-with-presets__input"
              required
              type="date"
              v-bind:value="dateTo ? $options.convertToInputDate(dateTo) : ''">
          </label>
          <button
            class="date-range-with-presets__select"
            type="button"
            v-bind:title="hasSelectedTo ? 'Изменить период' : 'Выбрать период'"
            v-on="listenerObject"
          >{{ hasSelectedTo ? '' : 'Выберите период' }}</button>
        </fieldset>
      </template>
    </date-picker-dialog>
    <date-range-presets
      v-bind:value="datePickerValue"
      v-bind:presets="presets"
      v-bind:button-clear-show="isShowButtonClear"
      v-on:input-preset="onInputPreset"
      v-on:click-clear="onClearDate"
    />
  </div>
</template>
<script>
import DatePickerDialog from '@/components/DatePickerDialog.vue';
import { convertToInputDate, formatDate } from '@/utils/timeFormat';
import DateRangePresets from '@/components/DateRangePresets.vue';

const DAY_MILLISECONDS = 24 * 60 * 60 * 1000;

const WEEK_MILLISECONDS = 24 * 60 * 60 * 1000 * 7;

/**
 * @description Предварительные периоды.
 */
const defaultPresets = [
  {
    id: 'tomorrow',
    label: 'Вчера',
    title: 'Показать отчет от вчерашнего дня',
    toValue: () => {
      const to = new Date();
      const from = new Date();
      from.setTime(from.getTime() - DAY_MILLISECONDS);
      to.setTime(to.getTime() - DAY_MILLISECONDS);
      return [from, to];
    },
  },
  {
    id: 'week',
    label: 'Неделя',
    title: 'Показать отчет от прошлой недели',
    toValue: () => {
      const to = new Date();
      const from = new Date();
      from.setTime(from.getTime() - WEEK_MILLISECONDS);
      return [from, to];
    },
  },
  {
    id: 'month',
    label: 'Месяц',
    title: 'Показать отчет от прошлого месяца',
    toValue: () => {
      const to = new Date();
      const currentMonth = to.getMonth();
      const from = new Date();
      from.setMonth(currentMonth - 1);
      return [from, to];
    },
  },
  {
    id: 'year',
    label: 'Год',
    title: 'Показать отчет от прошлого года',
    toValue: () => {
      const to = new Date();
      const currentYear = to.getFullYear();
      const from = new Date();
      from.setFullYear(currentYear - 1);
      return [from, to];
    },
  },
];

/**
 * @description Выбор диапазона дат с предварительными вариантами.
 * @type {Vue}
 */
export default {
  components: { DateRangePresets, DatePickerDialog },
  computed: {
    /**
     * @description Имеет выбранную дату окончания.
     * @type {boolean}
     */
    hasSelectedTo() {
      return this.datePickerValue.length > 0;
    },
    dateTo() {
      const [from, to] = this.datePickerValue;
      return to || from;
    },
    /**
     * @description CSS custom properties.
     * @type {object}
     */
    cssVars() {
      return {
        '--color-date': this.customColorDate,
      };
    },
  },
  convertToInputDate,
  data() {
    return {
      datePickerValue: this.value,
    };
  },
  formatDate,
  methods: {
    /**
     * @description Очистить дату.
     * @returns {void}
     */
    onClearDate() {
      this.datePickerValue = [];
      this.onInput([], []);
    },
    onInput(preset, updatedValue) {
      this.$emit('input', updatedValue);
      this.$emit('input-preset', preset);
    },
    onInputPreset(value) {
      const [updatedValue] = this.presets.flatMap(({ id, toValue }) => {
        if (id === value) {
          return [toValue()];
        }
        return [];
      });
      this.datePickerValue = updatedValue;
      this.onInput(value, updatedValue);
    },
    onDatePickerDialogInput(updatedValue) {
      if (updatedValue.length === 2) {
        this.datePickerValue = updatedValue.sort(
          (first, second) => Number(first.getTime() > second.getTime()),
        );
        this.onInput('range', this.datePickerValue);
      } else {
        this.datePickerValue = updatedValue;
        if (updatedValue.length === 1) {
          const [left] = updatedValue;
          this.onInput('range', [left, left]);
        }
        if (updatedValue.length === 0) {
          this.onInput('range', []);
        }
      }
    },
    onDatePickerDialogClose() {
      this.$emit('close');
    },
    /**
     * Полностью очистить поля
     *
     * @returns {void}
     */
    cleanEverything() {
      this.datePickerValue = [];
    },
  },
  name: 'DateRangeWithPresets',
  presetRadioName: 'date-range-with-presets-radio',
  props: {
    presets: {
      default: () => defaultPresets,
      required: false,
      type: Array,
    },
    value: {
      required: true,
      type: Array,
    },
    customColorDate: {
      type: String,
      default: () => '',
    },
    /**
     * @description Показывать ли кнопку очистки.
     * @type {Boolean}
     */
    isShowButtonClear: {
      default: false,
      type: Boolean,
    },
  },
  watch: {
    value(value) {
      this.datePickerValue = value;
    },
  },
};
</script>
<style lang="scss" scoped>
.date-range-with-presets {
  display: flex;
  flex-direction: row;
  gap: 1rem 2rem;
  &__date {
    &.custom-color {
      color: var(--color-date);
    }
  }
}
.date-range-with-presets .date-range-with-presets__date:not(.date-selected),
.date-range-with-presets .date-range-with-presets__input {
  opacity: 0;
}
.date-range-with-presets .date-range-with-presets__input {
  left: 0;
  position: absolute;
  width: 0;
}
.date-range-with-presets .date-range-with-presets__modes {
  align-items: center;
  border-radius: var(--input-border-radius);
  display: flex;
  flex-direction: row;
  overflow: clip;
}
.date-range-with-presets .date-range-with-presets__search {
  align-items: center;
  background: var(--primary-background-color);
  border-radius: var(--input-border-radius);
  border: 1px solid var(--border-color);
  display: flex;
  font-size: 1rem;
  font-weight: 400;
  height: 2.5rem;
  line-height: 1.1875rem;
  padding: 0 0 0 .625rem;
  position: relative;
  width: 15.625rem;
}
.date-range-with-presets .date-range-with-presets__search:after {
  background-color: var(--fill-color);
  background-image: url("@/assets/img/calendar-grey.svg");
  background-position: center;
  border-radius: 0 4px 4px 0;
  content: '';
  height: 38px;
  overflow: hidden;
  position: absolute;
  right: 0;
  width: 38px;
}
.date-range-with-presets .date-range-with-presets__select {
  height: inherit;
  left: 0;
  padding: inherit;
  position: absolute;
  text-align: inherit;
  width: inherit;
  z-index: 1;
}
.preset.active > .preset__label,
.preset > .preset__label:hover {
  background: var(--action-color);
  color: var(--primary-background-color);
}
.preset > .preset__input {
  opacity: 0;
  position: absolute;
  z-index: -1;
}
.preset > .preset__label {
  align-items: center;
  background: var(--fill-color);
  color: var(--reading-color);
  cursor: pointer;
  display: flex;
  font-size: .875rem;
  font-weight: 400;
  height: 2.5rem;
  justify-content: center;
  line-height: 1.065rem;
  padding: 0 .8125rem;
}
@media screen and (max-width: 600px) {
  .date-range-with-presets {
    flex-wrap: wrap;
  }
  .date-range-with-presets .date-range-with-presets__search {
    width: min(100%, 15.625rem);
  }
}
</style>
